import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  COURSES_UNAUTHORIZED_MESSAGE,
  fetchCoursesResilient,
} from '../courses/coursesApi';
import {
  collapseOpeningCoursesToHubs,
  groupCoursesByPhase,
  sortPhaseCourses,
} from '../courses/courseHelpers';
import { coursePhasePath, coursesRootPath } from '../courses/coursesUrl';
import type { CourseListItemDto, CoursePhase } from '../courses/types';
import { COURSE_PHASE_LABELS } from '../courses/types';
import { touchNavButtonSx, touchNavHeaderRowSx } from '../common/touchTargetSx';
import { courseSelectionGridSx } from '../courses/courseMuiStyles';
import { useUserSettings } from '../settings/useUserSettings';
import DashboardCourseCard from './DashboardCourseCard';
import DashboardSection from './DashboardSection';
import { dashboardSectionTitleSx } from './dashboardMuiStyles';

const PHASE_COURSE_LIMITS: Record<CoursePhase, number> = {
  opening: 4,
  middlegame: 1,
  endgame: 1,
  mistakes: 1,
};

const DASHBOARD_PHASES: readonly CoursePhase[] = [
  'opening',
  'middlegame',
  'endgame',
] as const;

const pickDashboardPhaseCourses = (
  courses: CourseListItemDto[],
  limit: number,
): CourseListItemDto[] => {
  const sorted = sortPhaseCourses(courses);

  const recent = sorted
    .filter((course) => course.lastAccessedAt)
    .sort(
      (a, b) =>
        new Date(b.lastAccessedAt ?? 0).getTime() -
        new Date(a.lastAccessedAt ?? 0).getTime(),
    )
    .slice(0, limit);

  const usedIds = new Set(recent.map((course) => course.courseId));
  const starterSlots = limit - recent.length;

  const starters =
    starterSlots > 0
      ? sorted
          .filter(
            (course) =>
              !course.lastAccessedAt && !usedIds.has(course.courseId),
          )
          .slice(0, starterSlots)
      : [];

  const selected = [...recent, ...starters];
  if (selected.length >= limit) {
    return selected.slice(0, limit);
  }

  const selectedIds = new Set(selected.map((course) => course.courseId));
  const fillers = sorted
    .filter((course) => !selectedIds.has(course.courseId))
    .slice(0, limit - selected.length);

  return [...selected, ...fillers];
};

type PhaseCoursesSectionProps = {
  phase: CoursePhase;
  courses: CourseListItemDto[];
};

const PhaseCoursesSection = ({ phase, courses }: PhaseCoursesSectionProps) => {
  const navigate = useNavigate();

  if (courses.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1.25}>
      <Box
        sx={{
          ...touchNavHeaderRowSx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography variant="subtitle1" component="h3" sx={dashboardSectionTitleSx}>
          {COURSE_PHASE_LABELS[phase]}
        </Typography>
        <Button
          size="small"
          onClick={() => navigate(coursePhasePath(phase))}
          sx={touchNavButtonSx}
        >
          View all
        </Button>
      </Box>
      <Box sx={courseSelectionGridSx(courses.length)}>
        {courses.map((course) => (
          <DashboardCourseCard key={course.courseId} course={course} />
        ))}
      </Box>
    </Stack>
  );
};

export const RecentCoursesSection = () => {
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const { settings } = useUserSettings();
  const [courses, setCourses] = useState<CourseListItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const { courses: allCourses } = await fetchCoursesResilient(refreshSession);
        if (!cancelled) {
          setCourses(allCourses);
        }
      } catch (error) {
        if (
          !cancelled &&
          !(
            error instanceof Error &&
            error.message === COURSES_UNAUTHORIZED_MESSAGE
          )
        ) {
          setErrorMessage(
            error instanceof Error ? error.message : 'Failed to load courses',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [refreshSession]);

  const coursesByPhase = useMemo(() => {
    const grouped = groupCoursesByPhase(courses);
    const openingHubs = collapseOpeningCoursesToHubs(
      grouped.opening,
      settings.whiteRepertoireForks,
    );
    return Object.fromEntries(
      DASHBOARD_PHASES.map((phase) => [
        phase,
        pickDashboardPhaseCourses(
          phase === 'opening' ? openingHubs : grouped[phase],
          PHASE_COURSE_LIMITS[phase],
        ),
      ]),
    ) as Record<CoursePhase, CourseListItemDto[]>;
  }, [courses, settings.whiteRepertoireForks]);

  const hasCourses = DASHBOARD_PHASES.some(
    (phase) => coursesByPhase[phase].length > 0,
  );

  return (
    <DashboardSection
      title="Courses"
      action={
        <Button
          size="small"
          onClick={() => navigate(coursesRootPath())}
          sx={touchNavButtonSx}
        >
          All courses
        </Button>
      }
    >
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && errorMessage && (
        <Alert severity="warning">{errorMessage}</Alert>
      )}

      {!loading && !errorMessage && !hasCourses && (
        <Typography variant="body2" color="text.secondary">
          No courses available yet.{' '}
          <Button
            size="small"
            onClick={() => navigate(coursesRootPath())}
            sx={touchNavButtonSx}
          >
            Browse courses
          </Button>
        </Typography>
      )}

      {!loading && !errorMessage && hasCourses && (
        <Stack spacing={2.5}>
          <PhaseCoursesSection
            phase="opening"
            courses={coursesByPhase.opening}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2.5,
              alignItems: 'start',
            }}
          >
            <PhaseCoursesSection
              phase="middlegame"
              courses={coursesByPhase.middlegame}
            />
            <PhaseCoursesSection
              phase="endgame"
              courses={coursesByPhase.endgame}
            />
          </Box>
        </Stack>
      )}
    </DashboardSection>
  );
};

export default RecentCoursesSection;
